import { useCallback, useState, useEffect } from "react";
import { useAuthHook } from "@/hooks/useAuth";
import { useAuth } from "../../context/AuthContext";
import { Property } from "../../data/models";
import PropertyCard from "../../components/valuation/PropertyCard";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/supervisor/Sidebar";
import Header from "@/components/supervisor/Header";

/* =========================
   ✅ DATE SANITIZER (KEY FIX)
========================= */
function sanitizePropertyDates<T extends Record<string, any>>(data: T): T {
  const dateFields = [
    "approvedDate",
    "valuationDate",
    "valuationAssignmentDate",
    "certifiedAt",
  ];

  const cleaned = { ...data };

  for (const field of dateFields) {
    if (cleaned[field] === "") {
      cleaned[field] = null;
    }
  }

  return cleaned;
}

/* =========================
   THE COMPONENT
========================= */
const ViewData = () => {
  const { user, isAuthenticated, isLoading } = useAuthHook();
  const [clientProperties, setClientProperties] = useState<Property[]>([]);
  const [isLoadingProperties, setIsLoadingProperties] = useState(true);
   const { logout, token: authToken } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  /* =========================
     THEME HOOK (LOCAL)
  ========================= */
  function useTheme() {
    const [theme, setThemeState] = useState<"light" | "dark">("light");
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
      const storedTheme =
        (localStorage.getItem("theme") as "light" | "dark") || "light";
      setThemeState(storedTheme);
      setMounted(true);
    }, []);

    const setTheme = useCallback((newTheme: "light" | "dark") => {
      setThemeState(newTheme);
      localStorage.setItem("theme", newTheme);
    }, []);

    useEffect(() => {
      if (mounted) {
        document.documentElement.classList.toggle("dark", theme === "dark");
      }
    }, [theme, mounted]);

    return { theme, setTheme };
  }

  const { theme, setTheme } = useTheme();

  const apiUrl =
    import.meta.env.VITE_API_BASE_URL || "https://valuate-api.vercel.app/api/v1";

  /* =========================
     FETCH PROPERTIES (FIXED)
  ========================= */
  useEffect(() => {
    const fetchProperties = async () => {
      if (!user) return;

      try {
        setIsLoadingProperties(true);
        setError(null);

        const token = localStorage.getItem("valuation_token");

        const response = await fetch(`${apiUrl}/properties`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch properties (${response.status})`);
        }

        const result = await response.json();

        let properties: Property[] = [];

        if (Array.isArray(result?.data)) {
          properties = result.data;
        } else if (Array.isArray(result?.data?.properties)) {
          properties = result.data.properties;
        }

        /* 🔥 CRITICAL FIX APPLIED HERE */
        setClientProperties(properties.map(sanitizePropertyDates));
      } catch (err) {
        console.error("Error fetching properties:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load properties"
        );
      } finally {
        setIsLoadingProperties(false);
      }
    };

    if (isAuthenticated) {
      fetchProperties();
    }
  }, [user, isAuthenticated, apiUrl]);

  /* =========================
     STATS
  ========================= */
  const totalValue = clientProperties.reduce(
    (sum, property) =>
      sum + (property?.totalPropertyValue || property?.totalValue || 0),
    0
  );

  const averageValue =
    clientProperties.length > 0 ? totalValue / clientProperties.length : 0;

  const taxEstimate = totalValue * 0.009;

  const onPropertySelect = (propertyId: string) => {
    navigate(`/supervisor-dashboard/view-data/property/${propertyId}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }
console.log("user:", user);
  /* =========================
     RENDER
  ========================= */
  return (
    <div className="bg-gray-100">
      <Header theme={theme} setTheme={setTheme} logout={logout} />

      <div className="flex">
        <Sidebar />

        <div className="flex-1 lg:ml-64 p-6 bg-gray-100 min-h-screen">
          {/* Welcome */}
          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <h2 className="text-lg font-medium text-gray-900">
              Welcome back, {user?.firstName || "User"}!
            </h2>
            <p className="text-gray-500">
              Here’s the latest overview of your properties.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Properties */}
          {isLoadingProperties ? (
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
              <p className="text-gray-500">Loading properties...</p>
            </div>
          ) : clientProperties.length === 0 ? (
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <p className="text-gray-500">
                You don’t have any registered properties yet.
              </p>
              <button
                onClick={() => navigate("/property/submit")}
                className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg"
              >
                Submit New Property
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {clientProperties.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  onClick={() => onPropertySelect(property.id)}
                  userRole={user?.role}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewData;
